import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import type { ChartVariant } from "./types";
import { VARIANT_CONFIG } from "./useChartModel";

export type ChartState = "ready" | "empty" | "streaming" | "error";

interface ChartFrameProps {
  variant: ChartVariant;
  title?: string;
  state?: ChartState;
  emptyLabel?: string;
  errorLabel?: string;
  /** Rendered below the plot (typically a `ChartLegend`). */
  legend?: ReactNode;
  className?: string;
  children: ReactNode;
}

function PlaceholderBody({
  height,
  children,
}: {
  height: number;
  children: ReactNode;
}) {
  return (
    <div
      className="flex items-center justify-center text-xs text-muted-foreground"
      style={{ height }}
    >
      {children}
    </div>
  );
}

/**
 * Glass container that gives every chart consistent chrome: optional title,
 * a fixed-height plot area, and empty / streaming / error fallbacks that keep
 * layout stable while data is missing or a Kai turn is still streaming.
 */
export function ChartFrame({
  variant,
  title,
  state = "ready",
  emptyLabel = "No data",
  errorLabel = "Couldn't render chart",
  legend,
  className,
  children,
}: ChartFrameProps) {
  const height = VARIANT_CONFIG[variant].height;

  return (
    <figure
      className={cn(
        "glass-panel my-2 flex flex-col gap-1.5 rounded-xl p-3",
        className,
      )}
    >
      {title && (
        <figcaption className="text-xs font-semibold text-foreground">
          {title}
        </figcaption>
      )}

      {state === "ready" ? (
        <>
          {children}
          {legend}
        </>
      ) : state === "streaming" ? (
        <PlaceholderBody height={height}>
          <span className="animate-pulse">Building chart…</span>
        </PlaceholderBody>
      ) : state === "error" ? (
        <PlaceholderBody height={height}>{errorLabel}</PlaceholderBody>
      ) : (
        <PlaceholderBody height={height}>{emptyLabel}</PlaceholderBody>
      )}
    </figure>
  );
}
